import {
  Block,
  List,
  ListInput,
  ListItem,
  Navbar,
  NavRight,
  Page,
  PageContent,
  Preloader,
  Link as F7Link,
  f7,
} from 'framework7-react';
import { useEffect, useRef, useState } from 'react';
import { useT } from '@/i18n';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { inputToMinor, moneyToInput } from '@/utils/money';
import { isDecimalInput, isIntegerInput, sanitizeNumericInput } from '@/utils/numericInput';
import {
  useCreateDeliveryZone,
  useDeliveryZones,
  useOrderTypeConfig,
  useUpdateDeliveryZone,
} from '../hooks/useOrderSetup';
import type { DeliveryZone } from '@restaurant/api-client';

// Operator mental model (FRONTEND_PRACTICES Rule 11): a delivery zone is
// "we deliver within N km, the fee is X, the minimum is Y". The schema's
// `mode` / `polygon` / `displayOrder` / `isActive` discriminators are never
// shown - a new zone is a distance band whose mode follows the delivery
// distanceCalculation (DIRECT → STRAIGHT_LINE, ROUTE → ROUTE_DISTANCE). A
// map-drawn (POLYGON) zone can still have its fee/minimum/travel edited here;
// its area is left untouched (partial update preserves it).
//
// Slide-in editor page, not a bottom sheet (FRONTEND_PRACTICES Rule 12).

const toast = (text: string) => f7.toast.show({ text, closeTimeout: 2000, position: 'bottom' });

/** Full-screen pushed editor for a delivery zone. Loads the zone by id from the
 *  cached list; `new` opens a blank form. */
export default function DeliveryZoneFormPage({
  f7route,
}: {
  f7route: { params: { zoneId?: string } };
}) {
  const { zoneId } = f7route.params;
  const isNew = !zoneId || zoneId === 'new';
  const t = useT();
  const { data: zones } = useDeliveryZones();
  const submitRef = useRef<() => void>(() => {});

  const bf = t.orderSetup.page.bandForm;
  const zone = isNew ? null : (zones?.find((z) => z.id === zoneId) ?? null);
  const ready = isNew || zone != null;
  const back = () => f7.views.current.router.back();

  return (
    <Page name="delivery-zone-form" pageContent={false}>
      <Navbar title={isNew ? bf.newTitle : bf.editTitle} backLink={bf.back}>
        <NavRight>
          <F7Link onClick={ready ? () => submitRef.current() : undefined}>{t.saveShort}</F7Link>
        </NavRight>
      </Navbar>
      {ready ? (
        <DeliveryZoneContent zone={zone} onDone={back} submitRef={submitRef} />
      ) : (
        <PageContent>
          <Block className="text-align-center" style={{ paddingTop: 48 }}>
            <Preloader />
          </Block>
        </PageContent>
      )}
    </Page>
  );
}

function DeliveryZoneContent({
  zone,
  onDone,
  submitRef,
}: {
  zone: DeliveryZone | null;
  onDone: () => void;
  submitRef: { current: () => void };
}) {
  const bf = useT().orderSetup.page.bandForm;
  const currency = useRestaurantStore((s) => s.restaurantConfig?.currency) ?? 'EUR';
  const create = useCreateDeliveryZone();
  const update = useUpdateDeliveryZone();
  const { data: configs } = useOrderTypeConfig();
  const isPolygon = zone?.mode === 'POLYGON';
  // A new distance tier inherits its shape from the restaurant's delivery
  // calculation: DIRECT → STRAIGHT_LINE (haversine), ROUTE → ROUTE_DISTANCE (MapBox).
  const createMode: 'STRAIGHT_LINE' | 'ROUTE_DISTANCE' =
    configs?.find((c) => c.orderType === 'DELIVERY')?.distanceCalculation === 'ROUTE'
      ? 'ROUTE_DISTANCE'
      : 'STRAIGHT_LINE';

  const [label, setLabel] = useState('');
  const [maxKm, setMaxKm] = useState('');
  const [fee, setFee] = useState('');
  const [minOrder, setMinOrder] = useState('');
  const [deliveryMins, setDeliveryMins] = useState('');
  // ROUTE_DISTANCE bands read their travel time from live driving data, so the
  // field would be a lie there. Every other shape has nothing to ask, so the
  // owner's number IS the promise.
  const zoneMode = zone?.mode ?? createMode;
  const needsDeliveryTime = zoneMode !== 'ROUTE_DISTANCE';
  // Synchronous in-flight guard so a rapid double-tap on Save can't create two
  // bands before the mutation's isPending flips.
  const submittingRef = useRef(false);

  // Populate from the loaded zone (or blank for a new one). `zone` is a stable
  // reference from the cached list, so this only re-runs on a real change.
  useEffect(() => {
    setLabel(zone?.label ?? '');
    setMaxKm(zone?.maxDistanceKm != null ? String(zone.maxDistanceKm) : '');
    setFee(zone ? moneyToInput(zone.feeCents, currency) : '');
    setMinOrder(zone ? moneyToInput(zone.minOrderCents, currency) : '');
    setDeliveryMins(zone?.deliveryMinutes != null ? String(zone.deliveryMinutes) : '');
  }, [zone, currency]);

  submitRef.current = () => {
    if (submittingRef.current) return;

    const name = label.trim();
    if (!name) {
      f7.dialog.alert(bf.nameRequired);
      return;
    }
    if (![maxKm, fee, minOrder].every(isDecimalInput)) {
      f7.dialog.alert(bf.invalidNumber);
      return;
    }
    if (needsDeliveryTime && !isIntegerInput(deliveryMins)) {
      f7.dialog.alert(bf.wholeMinutes);
      return;
    }
    const km = Number.parseFloat(maxKm.replace(',', '.'));
    const feeNum = Number.parseFloat(fee.replace(',', '.'));
    const minNum = Number.parseFloat(minOrder.replace(',', '.'));
    // Reject negatives loudly. Previously km gave a misleading "set km" message
    // and fee/minimum were silently clamped to 0 by inputToMinor - the operator
    // never learned their input was rejected. One clear "invalid, not saved".
    if (
      (Number.isFinite(km) && km < 0) ||
      (Number.isFinite(feeNum) && feeNum < 0) ||
      (Number.isFinite(minNum) && minNum < 0)
    ) {
      f7.dialog.alert(bf.invalidInput);
      return;
    }
    if (!isPolygon && (!Number.isFinite(km) || km <= 0)) {
      f7.dialog.alert(bf.kmRequired);
      return;
    }
    // Without it the guest gets a handoff time that silently omits the drive.
    const mins = Number.parseInt(deliveryMins, 10);
    if (needsDeliveryTime && (!Number.isFinite(mins) || mins < 0)) {
      f7.dialog.alert(bf.deliveryTimeRequired);
      return;
    }

    const shared = {
      label: name,
      feeCents: inputToMinor(fee, currency),
      minOrderCents: inputToMinor(minOrder, currency),
      ...(needsDeliveryTime ? { deliveryMinutes: mins } : {}),
    };
    submittingRef.current = true;
    const onSuccess = () => {
      toast(zone ? bf.updated : bf.added);
      onDone();
    };
    const onError = (e: unknown) => f7.dialog.alert(e instanceof Error ? e.message : bf.saveFailed);
    const onSettled = () => {
      submittingRef.current = false;
    };

    if (zone) {
      // Polygon zones keep their drawn area - only edit the priced fields.
      update.mutate(
        {
          zoneId: zone.id,
          data: isPolygon ? shared : { ...shared, maxDistanceKm: km },
        },
        { onSuccess, onError, onSettled },
      );
    } else {
      create.mutate(
        { ...shared, mode: createMode, maxDistanceKm: km },
        { onSuccess, onError, onSettled },
      );
    }
  };

  return (
    <PageContent>
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        <ListInput
          label={bf.nameLabel}
          type="text"
          placeholder={bf.namePlaceholder}
          value={label}
          outline={f7.theme !== 'ios'}
          floatingLabel={f7.theme !== 'ios'}
          onInput={(e) => setLabel((e.target as HTMLInputElement).value)}
        />
        {isPolygon ? (
          <ListItem title={bf.areaLabel} after={bf.areaAfter} footer={bf.areaFooter} />
        ) : (
          <ListInput
            label={bf.kmLabel}
            type="text"
            inputmode="decimal"
            placeholder={bf.kmPlaceholder}
            value={maxKm}
            outline={f7.theme !== 'ios'}
            floatingLabel={f7.theme !== 'ios'}
            errorMessage={bf.invalidNumber}
            errorMessageForce={!isDecimalInput(maxKm)}
            onInput={(e) => setMaxKm(sanitizeNumericInput((e.target as HTMLInputElement).value))}
          />
        )}
        <ListInput
          label={`${bf.feeLabel} (${currency})`}
          type="text"
          inputmode="decimal"
          placeholder={bf.feePlaceholder}
          value={fee}
          outline={f7.theme !== 'ios'}
          floatingLabel={f7.theme !== 'ios'}
          errorMessage={bf.invalidNumber}
          errorMessageForce={!isDecimalInput(fee)}
          onInput={(e) => setFee(sanitizeNumericInput((e.target as HTMLInputElement).value))}
        />
        {needsDeliveryTime && (
          <ListInput
            label={bf.deliveryTimeLabel}
            type="text"
            inputmode="numeric"
            placeholder={bf.deliveryTimePlaceholder}
            info={bf.deliveryTimeInfo}
            value={deliveryMins}
            outline={f7.theme !== 'ios'}
            floatingLabel={f7.theme !== 'ios'}
            errorMessage={bf.wholeMinutes}
            errorMessageForce={!isIntegerInput(deliveryMins)}
            onInput={(e) =>
              setDeliveryMins(sanitizeNumericInput((e.target as HTMLInputElement).value))
            }
          />
        )}
        <ListInput
          label={`${bf.minOrderLabel} (${currency})`}
          type="text"
          inputmode="decimal"
          placeholder={bf.minOrderPlaceholder}
          value={minOrder}
          outline={f7.theme !== 'ios'}
          floatingLabel={f7.theme !== 'ios'}
          errorMessage={bf.invalidNumber}
          errorMessageForce={!isDecimalInput(minOrder)}
          onInput={(e) => setMinOrder(sanitizeNumericInput((e.target as HTMLInputElement).value))}
        />
      </List>
    </PageContent>
  );
}
